import React, { useEffect, useState } from "react";
import { Headphones, Play, Calendar, User, BookOpen, X, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading, TiltCard } from "./Atoms";
import { api } from "../../lib/api";

// Convert a Drive share URL into an embeddable /preview URL.
function toDrivePreview(link) {
  if (!link) return null;
  // file/d/FILE_ID/...
  const m = link.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  // ?id=FILE_ID
  const m2 = link.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (m2) return `https://drive.google.com/file/d/${m2[1]}/preview`;
  // Folder or other — return null and let users open externally
  return null;
}

const Recordings = () => {
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null); // recording obj

  useEffect(() => {
    let mounted = true;
    api.get("/recordings")
      .then((r) => { if (mounted) setItems(r.data.recordings || []); })
      .catch(() => { if (mounted) setItems([]); });
    return () => { mounted = false; };
  }, []);

  const fmtDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
    catch { return d; }
  };

  return (
    <section id="recordings" className="relative py-28 px-6 bg-[#050505]">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <SectionHeading
            overline="Service Recordings"
            title={<>Listen. <span className="italic gold-text">Be stirred.</span> Burn again.</>}
            subtitle="Access recent teachings, prayer calls, revival services, and ministry recordings from Revival Fire Ministries Global. Tap any recording to play it in-page."
          />
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.length === 0 && (
            <div className="md:col-span-2 bento-card !p-12 text-center" data-testid="recordings-empty">
              <span className="glow-blob" />
              <span className="icon-badge mx-auto mb-5"><Headphones className="h-5 w-5 text-[#C9A44C]" /></span>
              <h3 className="font-serif-display text-3xl text-[#F6F0E6]">Recordings coming soon</h3>
              <p className="mt-3 text-[#D9D9D9] max-w-md mx-auto">
                Fresh teachings, prayer calls, and revival services are on the way.
                Join the Updates list and you&rsquo;ll be the first to know when they drop.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <a href="/join-updates" className="btn-gold !py-2.5 !px-5 !text-sm" data-testid="recordings-empty-join">Join Updates</a>
                <a href="/request-prayer" className="btn-ghost !py-2.5 !px-5 !text-sm">Request Prayer</a>
              </div>
            </div>
          )}
          {items.map((rec, i) => (
            <Reveal key={rec.id} delay={i * 90}>
              <TiltCard strength={3}>
                <article className="bento-card flex flex-col h-full min-h-[280px]" data-testid={`recording-${rec.id}`}>
                  <span className="glow-blob" />
                  <div className="flex items-start justify-between">
                    <span className="icon-badge"><Headphones className="h-5 w-5 text-[#C9A44C]" /></span>
                    <span className="text-[11px] tracking-[0.22em] uppercase text-[#C9A44C]">{rec.service_type}</span>
                  </div>
                  <h3 className="mt-8 font-serif-display text-3xl text-[#F6F0E6] leading-tight">{rec.title}</h3>
                  <p className="mt-3 text-[#D9D9D9] text-[15px] leading-relaxed line-clamp-3">{rec.description}</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-[#D9D9D9]/80">
                    <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-[#C9A44C]" />{rec.speaker}</span>
                    <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[#C9A44C]" />{fmtDate(rec.date)}</span>
                    {rec.scripture && <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-[#C9A44C]" />{rec.scripture}</span>}
                  </div>
                  <div className="mt-6 pt-5 border-t border-[#C9A44C]/15 flex items-center justify-between">
                    <button onClick={() => setPlaying(rec)} className="btn-ghost !py-2.5 !px-5 !text-sm" data-testid={`recording-listen-${rec.id}`}>
                      <Play className="h-3.5 w-3.5" strokeWidth={1.8} />
                      Listen / Watch
                    </button>
                    <span className="text-[10px] tracking-[0.28em] uppercase text-[#C9A44C]/70">RFMG</span>
                  </div>
                </article>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </div>

      {playing && (
        <div className="fixed inset-0 z-50 bg-[#050505]/90 backdrop-blur-md flex items-center justify-center px-4 py-6" onClick={() => setPlaying(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bento-card !p-5 max-w-4xl w-full" data-testid="player-modal">
            <span className="glow-blob" />
            <div className="flex items-start justify-between gap-4 mb-4 px-2">
              <div>
                <div className="overline">{playing.service_type}</div>
                <h3 className="font-serif-display text-2xl text-[#F6F0E6]">{playing.title}</h3>
                <div className="text-xs text-[#D9D9D9]/70 mt-1">{playing.speaker} · {fmtDate(playing.date)} {playing.scripture && `· ${playing.scripture}`}</div>
              </div>
              <button onClick={() => setPlaying(null)} className="h-9 w-9 rounded-full border border-[#C9A44C]/30 inline-flex items-center justify-center" data-testid="player-close"><X className="h-4 w-4" /></button>
            </div>
            {toDrivePreview(playing.link) ? (
              <div className="aspect-video w-full rounded-xl overflow-hidden border border-[#C9A44C]/20 bg-[#050505]">
                <iframe title={playing.title} src={toDrivePreview(playing.link)} allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
              </div>
            ) : (
              <div className="p-8 text-center text-[#D9D9D9]">
                This recording opens in Google Drive.
                <div className="mt-4">
                  <a href={playing.link} target="_blank" rel="noopener noreferrer" className="btn-fire">
                    <ExternalLink className="h-4 w-4" /> Open in Drive
                  </a>
                </div>
              </div>
            )}
            {playing.description && <p className="mt-5 text-sm text-[#D9D9D9] leading-relaxed px-2">{playing.description}</p>}
          </div>
        </div>
      )}
    </section>
  );
};

export default Recordings;
